/**
 * FreigabenView — W2 "Freigaben" decider inbox (PART C5).
 *
 * Lists the access requests the caller may DECIDE (GET /communications/access-requests
 * ?role=decider — requests on comms where the caller is a participant/full-access) and
 * offers Grant / Deny per row (POST …/access-requests/:id/decision).
 *
 * Each row carries the decider context the api projects for this list only (issue 33):
 * the mail's subject, the requester's name and their Begründung.
 *
 * Live-only surface: in mock mode the query stays disabled and the empty state renders
 * (no backend needed for the rest of Kommunikation).
 *
 * [Source: apps/web/src/features/communication-v2/hooks/useFreigabe.ts]
 * [Source: docs/design/2026-08-07_kommunikation-emailclient-tiers-freigabe.md#part-c5]
 */
import { ShieldCheck } from 'lucide-react';
import { isLiveData } from '../../../lib/data-mode';
import { useDeciderRequests } from '../hooks/useFreigabe';
import { FreigabeEntscheid } from './FreigabeEntscheid';

export function FreigabenView({
  onFeedback,
  onError,
}: {
  onFeedback?: (msg: string) => void;
  onError?: (msg: string) => void;
}) {
  const live = isLiveData();
  const { data: requests = [], isLoading, isError } = useDeciderRequests('pending');

  return (
    <div className="flex flex-col gap-3 rounded-xl border bg-card p-4">
      <div className="flex items-center gap-2">
        <ShieldCheck className="size-4 text-muted-foreground" />
        <h2 className="text-sm font-semibold text-foreground">Freigabe-Anfragen</h2>
      </div>
      <p className="text-xs text-muted-foreground">
        Anfragen auf Vollzugriff für E-Mails, an denen du beteiligt bist. Erteile oder lehne
        die Freigabe ab.
      </p>

      {!live ? (
        <div className="rounded-md border border-dashed p-6 text-center text-sm text-muted-foreground">
          Freigabe-Anfragen sind nur mit verbundenem Backend verfügbar.
        </div>
      ) : isLoading ? (
        <div className="p-6 text-center text-sm text-muted-foreground">Lade Anfragen …</div>
      ) : isError ? (
        <div role="alert" className="p-6 text-center text-sm text-muted-foreground">
          Anfragen konnten nicht geladen werden.
        </div>
      ) : requests.length === 0 ? (
        <div className="rounded-md border border-dashed p-6 text-center text-sm text-muted-foreground">
          Keine offenen Freigabe-Anfragen.
        </div>
      ) : (
        <ul className="divide-y divide-border overflow-hidden rounded-md border">
          {requests.map((r) => (
            <li key={r.id} className="flex flex-col gap-2 px-3 py-3 text-sm">
              <FreigabeEntscheid request={r} onFeedback={onFeedback} onError={onError} />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
